<template>
  <!-- Matches the AngularJS sidebar around it: its inset (common-filter-container),
       label-left checkbox rows, and tighter field padding so names fit the generic
       report's narrower column. The legacy `.ts` placeholder colour is already
       translucent; Vuetify's extra opacity on top made it unreadable. -->
  <div
    class="common-filter-container flex flex-col gap-4 pb-4 [&_.v-field]:text-sm [&_.v-field]:[--v-field-padding-start:8px] [&_.v-field]:[--v-field-padding-end:4px] [&_.v-field\_\_outline]:!text-gray-300 [&_input::placeholder]:!opacity-100 [&_div:has(>.v-checkbox)]:flex-row-reverse [&_div:has(>.v-checkbox)]:justify-between"
    data-cy="report-filter-panel"
  >
    <LlSelect
      v-if="showDistribution"
      label="Distribution"
      label-class="py-1 text-sm"
      :model-value="selectedDistribution"
      :items="distributionItems"
      item-title="title"
      placeholder="Distribution"
      clearable
      hide-details
      data-cy="report-filter-distribution"
      @update:model-value="setDistribution"
    />

    <div v-if="show.dateRange">
      <LlSelect
        v-if="!distributionLocksPeriod"
        label="Time Period"
        label-class="py-1 text-sm"
        :model-value="filters.dateRange ?? undefined"
        :items="store.periodOptions"
        item-value="label"
        placeholder="Select Time Period"
        hide-details
        data-cy="report-filter-period"
        @update:model-value="store.setFilters({ dateRange: $event })"
      />
      <LlSelect
        v-else
        label="Time Period"
        label-class="py-1 text-sm"
        :items="[]"
        placeholder="Distribution"
        disabled
        hide-details
      />
    </div>

    <div
      v-if="
        show.dateRange && filters.dateRange?.custom && !distributionLocksPeriod
      "
      class="grid grid-cols-2 gap-2"
    >
      <LlDatePicker
        label="Start"
        label-class="py-1 text-sm"
        :model-value="filters.dateRange.range.startDate"
        :show-button="false"
        data-cy="report-filter-start"
        @update:model-value="store.setCustomDate('startDate', $event)"
      />
      <LlDatePicker
        label="End"
        label-class="py-1 text-sm"
        :model-value="filters.dateRange.range.endDate"
        :show-button="false"
        data-cy="report-filter-end"
        @update:model-value="store.setCustomDate('endDate', $event)"
      />
    </div>

    <MatterInvolvingFilter
      v-if="show.matterForUser"
      horizontal
      :form="involvingForm"
      :accounts="panel.accounts"
      :lock-account-selection="panel.lockAccountSelection"
      @matter-involving-change="onMatterInvolvingChange"
    />

    <LlSelect
      v-if="show.archivedStatus || show.matterForUser"
      label="Show Archived"
      label-class="py-1 text-sm"
      :model-value="filters.status ?? undefined"
      :items="store.archivedOptions"
      hide-details
      data-cy="report-filter-archived"
      @update:model-value="store.setFilters({ status: $event })"
    />

    <LlPracticeAreaSelect
      v-if="show.matterTags"
      :model-value="filters.matterTag"
      placeholder="Filter by Practice Area"
      clearable
      @update:model-value="store.setFilters({ matterTag: $event ?? null })"
    />

    <LlSelect
      v-if="show.billed && !distributionHidesBilled"
      label="Show Billed"
      label-class="py-1 text-sm"
      :model-value="filters.billed ?? undefined"
      :items="store.billedOptions"
      hide-details
      data-cy="report-filter-billed"
      @update:model-value="store.setFilters({ billed: $event })"
    />

    <LlSelect
      v-if="show.contingencyBilled"
      label="Show Billed"
      label-class="py-1 text-sm"
      :model-value="filters.contingencyBilled ?? undefined"
      :items="store.billedOptions"
      hide-details
      data-cy="report-filter-contingency-billed"
      @update:model-value="store.setFilters({ contingencyBilled: $event })"
    />

    <LlSelect
      v-if="show.trustAccount && panel.trustAccounts.length > 1"
      label="Select Account"
      label-class="py-1 text-sm"
      :model-value="filters.trustAccount ?? undefined"
      :items="panel.trustAccounts"
      item-title="name"
      hide-details
      data-cy="report-filter-trust-account"
      @update:model-value="store.setTrustAccount($event)"
    />

    <template v-if="show.itemType">
      <v-divider v-if="show.matterForUser || show.dateRange" />
      <LlCheckbox
        :model-value="filters.showEntries"
        label="Show Time"
        @update:model-value="store.setFilters({ showEntries: $event })"
      />
      <LlCheckbox
        :model-value="filters.showCharges"
        label="Show Fixed Fees"
        @update:model-value="store.setFilters({ showCharges: $event })"
      />
      <LlCheckbox
        :model-value="filters.showExpenses"
        label="Show Expenses"
        @update:model-value="store.setFilters({ showExpenses: $event })"
      />
    </template>

    <template v-if="show.includeBilled">
      <v-divider />
      <LlCheckbox
        :model-value="filters.includeBilled"
        label="Include Billed"
        @update:model-value="store.setFilters({ includeBilled: $event })"
      />
    </template>

    <template v-if="show.originationUser">
      <v-divider />
      <LlCheckbox
        :model-value="filters.originationUser"
        label="Group by Users"
        @update:model-value="store.setFilters({ originationUser: $event })"
      />
    </template>

    <template v-if="show.showExpense">
      <v-divider />
      <LlCheckbox
        :model-value="filters.showExpense"
        label="Show Expenses"
        @update:model-value="store.setFilters({ showExpense: $event })"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { format } from 'date-fns';
import LlCheckbox from '@/components/LlCheckbox.vue';
import LlDatePicker from '@/components/LlDatePicker.vue';
import LlPracticeAreaSelect from '@/components/LlPracticeAreaSelect.vue';
import LlSelect from '@/components/LlSelect.vue';
import MatterInvolvingFilter from '@/components/MatterInvolvingFilter.vue';
import { useMeStore } from '@/services/UserService/useMeStore';
import { useReportFiltersStore } from '@/stores/reports/useReportFiltersStore';
import type { ReportUser } from '@/models/reportFilterTypes';

defineOptions({ inheritAttrs: false });

const store = useReportFiltersStore();
const { filters, panel } = storeToRefs(store);
const { features } = storeToRefs(useMeStore());

const show = computed(() => panel.value.show);

const showDistribution = computed(() =>
  Boolean(show.value.distribution && features.value?.distribution),
);

const distributionLocksPeriod = computed(
  () => showDistribution.value && store.hasDistribution,
);

// The generic engine hides billed status under a distribution; standard reports instead
// tell their controllers via the payload's `show.billed`.
const distributionHidesBilled = computed(
  () => panel.value.family === 'generic' && distributionLocksPeriod.value,
);

const distributionItems = computed(() =>
  panel.value.distributions.map((d) => ({
    id: d.id,
    title: `${d.label} ( ${format(new Date(d.date), 'MM/dd/yy')} )`,
  })),
);

const selectedDistribution = computed(() =>
  distributionItems.value.find((d) => d.id === filters.value.distributionId),
);

function setDistribution(item: { id: string } | null | undefined) {
  store.setFilters({ distributionId: item?.id ?? null });
}

const involvingForm = reactive({
  scopeAccount: filters.value.scopeAccount as ReportUser | null,
  primary: filters.value.primary,
  originating: filters.value.originating,
  reviewer: filters.value.reviewer,
});

watch(
  () => [
    filters.value.scopeAccount,
    filters.value.primary,
    filters.value.originating,
    filters.value.reviewer,
  ],
  ([scopeAccount, primary, originating, reviewer]) => {
    Object.assign(involvingForm, {
      scopeAccount,
      primary,
      originating,
      reviewer,
    });
  },
);

function onMatterInvolvingChange(form: typeof involvingForm) {
  const { scopeAccount, primary, originating, reviewer } = form;
  // MatterInvolvingFilter unticks the role boxes when the user is cleared; the reports
  // always kept them, so picking a user again restores the previous roles.
  if (scopeAccount) store.setFilters({ primary, originating, reviewer });
  // Through the action, so a deliberate "no filter" is never overwritten by the default.
  if ((scopeAccount?.id ?? null) !== (filters.value.scopeAccount?.id ?? null)) {
    store.setScopeAccount(scopeAccount ?? null);
  }
  Object.assign(involvingForm, {
    scopeAccount: filters.value.scopeAccount,
    primary: filters.value.primary,
    originating: filters.value.originating,
    reviewer: filters.value.reviewer,
  });
}
</script>
